/**
 * Shared character validator — pure, framework-free.
 *
 * Single source of truth for character validation rules.
 * Consumed by:
 *   - Nuxt (stores/character.ts) via direct TypeScript import.
 *   - Cloud Functions (functions/src/characters-api.ts) via the
 *     compiled shared/*.js emitted by `npm run build:shared`.
 *   - Foundry VTT (foundary_vtt/module/actor/character-sheet.mjs)
 *     via the same compiled output, through a symlink.
 *
 * Rules: NO Vue/Nuxt/Pinia imports. NO cross-file type imports from
 * `models/` — the models dep graph pulls in Firestore types and
 * unrelated Corporation code we don't want dragged into the shared
 * build. The minimal shape of each thing the validator touches is
 * declared locally below and structurally matches the canonical
 * model types in `models/*.ts`.
 *
 * Enum *values* (e.g. FeatureType.skill) are compared as string
 * literals so the runtime does not need the FeatureType enum to exist.
 */

// ---------------------------------------------------------------------
// Local minimal types — structurally compatible with models/*.ts.
// Intentionally narrow: only the fields the validator reads.
// ---------------------------------------------------------------------

/** Matches FeatureType in models/feature.ts — kept as string for framework-free runtime. */
export type FeatureTypeString = string;

/** Minimal shape of FeatureOption used by the validator. */
interface ValidatorFeatureOption {
	id: string;
	name: string;
	type: FeatureTypeString;
	/** Tag string used when building the character-tag set for requires checks. */
	tag?: string;
}

/** Minimal shape of Feature (blueprint node) used by the validator. */
interface ValidatorFeature {
	id?: string;
	name: string;
	type: FeatureTypeString;
	subFeatures: ValidatorFeature[];
	qty?: number;
	selectionRules?: { min: number; max: number };
	memberRefs?: unknown[];
	_memberRefIds?: string[];
	effects?: Array<{ type: string; value: string }>;
	/**
	 * Origin-gating: if present and non-empty, the character's tag set must
	 * satisfy at least one AND-group in this list. See `meetsRequires`.
	 * Example: `['mutant']` means "only applies to characters with the
	 * 'mutant' tag (e.g. Mutant Biology for Mutant-origin characters).
	 */
	requires?: string[];
}

/** Minimal shape of LevelDetail used by the validator. */
interface ValidatorLevelDetail {
	features: ValidatorFeatureOption[];
}

/** Minimal shape of Character used by the validator. */
interface ValidatorCharacter {
	/** Current rank — used to generate `rankN` tags for the character-tag set. */
	rank: number;
	details: ValidatorLevelDetail[];
}

/** Minimal shape of the per-rank blueprint — array of features per rank. */
type ValidatorBlueprint = ValidatorFeature[][];

export interface ValidationError {
	rank: number;
	featureType: FeatureTypeString;
	message: string;
}

interface SelectionCounts {
	min?: number;
	max?: number;
	num?: number;
}

/**
 * Build the set of tags that identify a character for `requires` matching.
 * Mirrors the Pinia getter `characterTags` in `stores/character.ts`.
 *
 * Tag sources:
 *   1. `rank0` .. `rank{character.rank}` — one tag per rank up to current.
 *   2. Every `feature.tag` from every rank's selected features.
 *   3. For core-type features, the class-specific tag stripped from the id
 *      (e.g. `core-merc` → `merc`, `core-sci-ops` → `sci-ops`).
 */
export function buildCharacterTags(character: ValidatorCharacter): Set<string> {
	const tags = new Set<string>();

	for (let r = 0; r <= character.rank; r++) {
		tags.add(`rank${r}`);
	}

	for (const detail of character.details) {
		for (const feature of detail?.features ?? []) {
			if (feature.tag) {
				tags.add(feature.tag);
			}
			if (feature.type === 'core' && feature.id?.startsWith('core-')) {
				tags.add(feature.id.replace('core-', ''));
			}
		}
	}

	return tags;
}

/**
 * Check whether a feature's `requires` gate is satisfied by the character's
 * current tag set. Mirrors `meetsRequires` in `models/property.ts`.
 *
 * Grammar (OR of ANDs):
 *   - `undefined` / `[]` → true (no gate).
 *   - `['human']` → character must have `human`.
 *   - `['human&&rank3']` → character must have BOTH `human` AND `rank3`.
 *   - `['human&&rank3', 'mutant&&rank1']` → first OR second.
 *   - `['skill-xyz>=5']` → character must have 5+ SP in skill-xyz.
 *     NOT supported here (we have no SP context in the shared validator);
 *     threshold checks always return false. Features with SP-gated requires
 *     will be filtered out. If that ever matters for validation, thread a
 *     `getSkillSP` callback through the signature.
 */
export function meetsRequires(
	requires: string[] | undefined,
	characterTags: Set<string>,
): boolean {
	if (!requires || requires.length === 0) return true;
	return requires.some((andGroup) => {
		return andGroup.split('&&').every((part) => {
			// Threshold syntax `skill-xyz>=5` — not supported without an SP
			// context; always fails. Features using this will be skipped.
			if (/>=\d+$/.test(part)) return false;
			return characterTags.has(part);
		});
	});
}

/**
 * Derive min / max / num selection rules from a blueprint Feature.
 *
 * Priority order (matches the Pinia implementation in stores/character.ts):
 *   1. `selectionRules` on the feature itself (new system).
 *   2. `qty` field (used by the default blueprint).
 *   3. Legacy `effects[]` with minRequired / maxRequired / numRequired.
 */
export function getFeatureSelectionRules(
	feature: ValidatorFeature,
): SelectionCounts {
	const rules: SelectionCounts = {};

	// Check new system first (selectionRules on feature)
	if (feature.selectionRules) {
		if (
			feature.selectionRules.min === feature.selectionRules.max &&
			feature.selectionRules.min > 0
		) {
			rules.num = feature.selectionRules.min;
		} else {
			if (feature.selectionRules.max > 0) {
				rules.max = feature.selectionRules.max;
			}
			if (feature.selectionRules.min > 0) {
				rules.min = feature.selectionRules.min;
			}
		}
		return rules;
	}

	// Fall back to qty field (used in default blueprint)
	if (feature.qty !== undefined && feature.qty !== null && feature.qty > 0) {
		rules.num = feature.qty;
		return rules;
	}

	// Fall back to legacy system (effects)
	if (feature.effects && feature.effects.length > 0) {
		for (const effect of feature.effects) {
			switch (effect.type) {
				case 'minRequired':
					rules.min = parseInt(effect.value);
					break;
				case 'maxRequired':
					rules.max = parseInt(effect.value);
					break;
				case 'numRequired':
					rules.num = parseInt(effect.value);
					break;
			}
		}
	}

	return rules;
}

/**
 * Return the FeatureOptions in `rankDetails` that belong to this blueprint feature.
 *
 * Handles the memberRefs edge case: when a blueprint feature has `memberRefs`
 * (or the bundle-hydrated `_memberRefIds`), selections are scoped to the
 * referenced ids only — so sibling sub-features that share a type (e.g.
 * Mutant Biology manipulation + sensory) are counted independently.
 *
 * Mirrors the Pinia getter `getSelectionsForFeature` — kept identical so
 * drift is impossible. Firestore round-trips may produce either `memberRefs`
 * (DocumentReference shape) or `_memberRefIds` (plain strings); both paths
 * are handled.
 */
export function getSelectionsForFeature(
	feature: ValidatorFeature,
	rankDetails: ValidatorLevelDetail | undefined,
): ValidatorFeatureOption[] {
	const all: ValidatorFeatureOption[] = rankDetails?.features || [];
	const hasMemberRefs =
		(feature.memberRefs && feature.memberRefs.length > 0) ||
		(feature._memberRefIds && feature._memberRefIds.length > 0);

	if (hasMemberRefs) {
		const memberIds = new Set<string>();
		if (feature._memberRefIds?.length) {
			for (const id of feature._memberRefIds) memberIds.add(id);
		} else {
			for (const ref of feature.memberRefs as unknown as Array<
				Record<string, unknown>
			>) {
				// DocumentReference shape varies across client / admin / bundle round-trips:
				//   .id                               — most common
				//   ._key.path.segments (last)        — client SDK internals
				//   ._path.segments (last)            — admin SDK internals
				const refAny = ref as {
					id?: string;
					_key?: { path?: { segments?: string[] } };
					_path?: { segments?: string[] };
				};
				const id =
					refAny?.id ??
					refAny?._key?.path?.segments?.slice(-1)?.[0] ??
					refAny?._path?.segments?.slice(-1)?.[0];
				if (id) memberIds.add(id);
			}
		}
		return all.filter(
			(f) => f.type === feature.type && memberIds.has(f.id),
		);
	}

	return all.filter((f) => f.type === feature.type);
}

/**
 * Count the character's "effective selections" for a blueprint feature.
 *
 * A blueprint feature can be one of three things:
 *
 *   1. **A leaf selector** (no sub-features): count features of its own
 *      type. Example: a rank 1 "corp" blueprint feature — character must
 *      pick 1 corp-typed feature.
 *
 *   2. **A direct selector with sub-features** (character has features
 *      matching the parent's type): count features of the parent's type,
 *      ignore children for this count. Example: "origin" parent — the
 *      character picks 1 origin directly, sub-features (homeWorld, feat)
 *      are independent categories validated on their own.
 *
 *   3. **A container** (has sub-features, but NO character features
 *      match the parent's own type): count features matching ANY of the
 *      applicable sub-features' types. Example: "Legacy Choices"
 *      (type=legacyChoices) — no feature is directly typed legacyChoices,
 *      the 2-budget is filled by the sub-features' types (knack +
 *      legacyItem).
 *
 * Sub-features with `memberRefs` get scoped by those refs via
 * `getSelectionsForFeature` — critical for Mutant Biology sibling
 * sub-features that all share `type=biology`.
 */
function countEffectiveSelections(
	feature: ValidatorFeature,
	rankDetails: ValidatorLevelDetail,
	tags: Set<string>,
): number {
	// Direct selections: memberRefs-scoped if the feature has any.
	const direct = getSelectionsForFeature(feature, rankDetails).length;
	if (direct > 0 || feature.subFeatures.length === 0) {
		return direct;
	}

	// Container: sum selections matching any applicable child type.
	const applicableSubs = feature.subFeatures.filter((sf) =>
		meetsRequires(sf.requires, tags),
	);
	if (applicableSubs.length === 0) return 0;

	const childTypes = new Set(applicableSubs.map((sf) => sf.type));
	return rankDetails.features.filter((f) => childTypes.has(f.type)).length;
}

/**
 * Apply the standard min / max / num checks against a count and push any
 * errors produced. The blueprint feature's name is used only for messages.
 */
function checkCountAgainstRules(
	count: number,
	rules: SelectionCounts,
	rank: number,
	featureType: FeatureTypeString,
	featureName: string,
	errors: ValidationError[],
): void {
	if (rules.min !== undefined && count < rules.min) {
		errors.push({
			rank,
			featureType,
			message: `${featureName}: minimum ${rules.min} required, but only ${count} selected`,
		});
	}
	if (rules.max !== undefined && count > rules.max) {
		errors.push({
			rank,
			featureType,
			message: `${featureName}: maximum ${rules.max} allowed, but ${count} selected`,
		});
	}
	if (rules.num !== undefined && count !== rules.num) {
		errors.push({
			rank,
			featureType,
			message: `${featureName}: exactly ${rules.num} required, but ${count} selected`,
		});
	}
}

/**
 * Walk the character's details array rank-by-rank, check each rank's
 * selections against the blueprint rules for that rank, and return the
 * list of validation errors. Empty array means valid.
 *
 * Two passes per top-level blueprint feature:
 *
 *   1. Apply the feature's own min/max/num against its effective count
 *      (direct type count, OR container sum — see countEffectiveSelections).
 *   2. Apply each applicable sub-feature's rules the same way — so sub-
 *      features that are themselves containers (Legacy Choices) get the
 *      container treatment instead of a dead-type-count check.
 */
export function validateCharacter(
	character: ValidatorCharacter,
	blueprints: ValidatorBlueprint,
): ValidationError[] {
	const errors: ValidationError[] = [];

	// Build the character's tag set once — used to filter out origin-gated
	// blueprint features (Mutant Biology etc.) that don't apply.
	const tags = buildCharacterTags(character);

	// Validate each rank.
	for (let rank = 0; rank < character.details.length; rank++) {
		// Blueprints may be shorter than character.details (new ranks added
		// to the character past the blueprint's known ranks). Skip cleanly.
		if (rank < 0 || rank >= blueprints.length) continue;
		const blueprintFeatures = blueprints[rank];
		if (!blueprintFeatures) continue;

		const rankDetails = character.details[rank];

		for (const blueprintFeature of blueprintFeatures) {
			// Skills and skill tricks use dynamic validation, skip standard rules.
			if (
				blueprintFeature.type === 'skill' ||
				blueprintFeature.type === 'skillTrick'
			) {
				continue;
			}

			// TODO: Remove corp filter when Corp Blueprint feature is ready.
			// Mirrors the same filter in stores/features.ts:102 — the corp
			// blueprint exists but the character editor doesn't prompt for
			// it yet, so no existing character has a corp selection and
			// validating it would reject every character.
			if (blueprintFeature.type === 'corp') continue;

			// Origin/rank gating: skip features whose `requires` isn't met.
			// Mirrors the Nuxt feature-tree filter in stores/features.ts:103.
			if (!meetsRequires(blueprintFeature.requires, tags)) continue;

			// --- Top-level rules on effective count ---
			const rules = getFeatureSelectionRules(blueprintFeature);
			const count = countEffectiveSelections(
				blueprintFeature,
				rankDetails,
				tags,
			);
			checkCountAgainstRules(
				count,
				rules,
				rank,
				blueprintFeature.type,
				blueprintFeature.name,
				errors,
			);

			// --- Per-sub-feature rules ---
			if (blueprintFeature.subFeatures.length > 0) {
				const applicableSubFeatures = blueprintFeature.subFeatures.filter(
					(sf) =>
						// TODO: remove corp filter when Corp Blueprint is ready.
						sf.type !== 'corp' && meetsRequires(sf.requires, tags),
				);
				for (const subFeature of applicableSubFeatures) {
					const subRules = getFeatureSelectionRules(subFeature);
					const subCount = countEffectiveSelections(
						subFeature,
						rankDetails,
						tags,
					);
					checkCountAgainstRules(
						subCount,
						subRules,
						rank,
						subFeature.type,
						subFeature.name,
						errors,
					);
				}
			}
		}
	}

	return errors;
}
